import type { VesselDrawState } from './types';

/**
 * Small motorboat: an open plate-alloy centre-console tinnie after the Quintrex
 * 540 — white hull, reverse-chine strake, black rubrail, centre console with a
 * grey leaning post, stainless rails and a single all-black outboard. Bow right.
 */
export const drawMotorSmall = ({ palette, night }: VesselDrawState): void => {
  const { ctx } = palette;
  const c = (hex: string): string => palette.colour(hex);

  // hull (white plate alloy, deep-V, level sheer to a fine pointed bow)
  ctx.beginPath();
  ctx.moveTo(-46, -7);
  ctx.lineTo(-30, -7.5);
  ctx.quadraticCurveTo(6, -8, 40, -8);
  ctx.quadraticCurveTo(50, -8, 56, -8.6); // level sheer to a pointed bow
  ctx.quadraticCurveTo(60, -8, 59, -4); // bow stem
  ctx.quadraticCurveTo(56, -1, 49, 1.5); // fine entry forefoot
  ctx.quadraticCurveTo(40, 4, 12, 6); // deep-V bottom
  ctx.quadraticCurveTo(-16, 7, -42, 5);
  ctx.lineTo(-46, 1);
  ctx.closePath();
  const hull = ctx.createLinearGradient(0, -9, 0, 6);
  hull.addColorStop(0, c('F7F9FB'));
  hull.addColorStop(0.5, c('E7ECF0'));
  hull.addColorStop(1, c('CDD5DC'));
  ctx.fillStyle = hull;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = c('9AA6B0');
  ctx.stroke();

  // reverse-chine strake + soft highlight
  ctx.beginPath();
  ctx.moveTo(-44, -1.6);
  ctx.quadraticCurveTo(0, -2.6, 44, -3.4);
  ctx.quadraticCurveTo(52, -4.2, 58, -6.4);
  ctx.strokeStyle = c('B7C0C9');
  ctx.lineWidth = 2.2;
  ctx.globalAlpha = 0.8;
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.moveTo(-44, -0.6);
  ctx.quadraticCurveTo(0, -1.6, 44, -2.4);
  ctx.quadraticCurveTo(52, -3.2, 58, -5.8);
  ctx.strokeStyle = c('FFFFFF');
  ctx.globalAlpha = 0.5;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // black rubrail along the gunwale
  ctx.beginPath();
  ctx.moveTo(-46, -7);
  ctx.lineTo(-30, -7.5);
  ctx.quadraticCurveTo(6, -8, 40, -8);
  ctx.quadraticCurveTo(50, -8, 56, -8.6);
  ctx.strokeStyle = c('20242A');
  ctx.lineWidth = 1.8;
  ctx.stroke();

  // single all-black outboard at the transom
  const outboard = ctx.createLinearGradient(-56, -5, -46, 2);
  outboard.addColorStop(0, c('30363C'));
  outboard.addColorStop(1, c('14171B'));
  ctx.fillStyle = outboard;
  ctx.beginPath();
  ctx.moveTo(-46, -5);
  ctx.lineTo(-53, -5);
  ctx.quadraticCurveTo(-56, -5, -56, -1.5);
  ctx.lineTo(-56, 2);
  ctx.lineTo(-46, 2);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = c('0E1013');
  ctx.lineWidth = 0.6;
  ctx.stroke();
  ctx.strokeStyle = c('3C434A');
  ctx.lineWidth = 0.7;
  ctx.globalAlpha = 0.7;
  ctx.beginPath();
  ctx.moveTo(-55, -0.6);
  ctx.lineTo(-47, -0.6);
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.fillStyle = c('17191C');
  ctx.beginPath();
  ctx.moveTo(-54, 2);
  ctx.lineTo(-49, 2);
  ctx.lineTo(-50, 11);
  ctx.lineTo(-52.5, 11);
  ctx.closePath();
  ctx.fill(); // leg
  ctx.fillStyle = c('20262C');
  ctx.fillRect(-53.5, 10.5, 4, 1.6); // cavitation plate

  // centre console (light grey) with low windscreen + grab rail
  ctx.beginPath();
  ctx.moveTo(-2, -8.5);
  ctx.lineTo(-2, -18);
  ctx.quadraticCurveTo(-2, -20, 1, -20);
  ctx.lineTo(9, -20);
  ctx.lineTo(14, -15);
  ctx.lineTo(14, -8.5);
  ctx.closePath();
  const console2 = ctx.createLinearGradient(0, -20, 0, -8.5);
  console2.addColorStop(0, c('EDF1F4'));
  console2.addColorStop(1, c('BEC7CE'));
  ctx.fillStyle = console2;
  ctx.fill();
  ctx.strokeStyle = c('8A98A2');
  ctx.lineWidth = 0.7;
  ctx.stroke();
  ctx.fillStyle = c('2A2F35');
  ctx.beginPath();
  ctx.moveTo(3, -15);
  ctx.lineTo(11, -15);
  ctx.lineTo(12, -12);
  ctx.lineTo(3, -12);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = c('AFD6EE');
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(9, -20);
  ctx.lineTo(14, -16);
  ctx.stroke();
  ctx.strokeStyle = c('D2DAE0');
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(2, -20);
  ctx.lineTo(2, -22);
  ctx.lineTo(8, -22);
  ctx.lineTo(8, -20);
  ctx.stroke();

  // grey leaning post / seat behind the console
  ctx.fillStyle = c('5A626A');
  ctx.beginPath();
  ctx.moveTo(-15, -8.5);
  ctx.lineTo(-15, -14);
  ctx.lineTo(-11, -14);
  ctx.lineTo(-11, -8.5);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = c('6A727A');
  ctx.beginPath();
  ctx.moveTo(-16, -14);
  ctx.quadraticCurveTo(-16, -16, -13, -16);
  ctx.lineTo(-6, -16);
  ctx.lineTo(-6, -13.5);
  ctx.lineTo(-13, -13.5);
  ctx.quadraticCurveTo(-16, -13.5, -16, -14);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = c('3C424A');
  ctx.beginPath();
  ctx.moveTo(-6, -16);
  ctx.lineTo(-3.5, -16);
  ctx.lineTo(-3.5, -9);
  ctx.lineTo(-6, -9);
  ctx.closePath();
  ctx.fill();

  // dark rocket-launcher frame with rod holders behind the seat
  ctx.strokeStyle = c('2A2E33');
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(-12, -15.5);
  ctx.lineTo(-14, -22);
  ctx.lineTo(-7, -23);
  ctx.stroke();
  ctx.lineWidth = 1.1;
  ctx.beginPath();
  ctx.moveTo(-13, -22);
  ctx.lineTo(-15, -26);
  ctx.moveTo(-10.5, -22.4);
  ctx.lineTo(-12, -26.5);
  ctx.moveTo(-8, -22.8);
  ctx.lineTo(-9, -27);
  ctx.stroke();

  // thin whip antenna
  ctx.strokeStyle = c('3A434B');
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.moveTo(-9, -20);
  ctx.lineTo(-7, -32);
  ctx.stroke();

  // stainless bow rail wrapping the pointed bow + stanchions
  ctx.strokeStyle = c('D2DAE0');
  ctx.lineWidth = 0.9;
  ctx.globalAlpha = 0.9;
  ctx.beginPath();
  ctx.moveTo(20, -8.5);
  ctx.quadraticCurveTo(40, -9, 56, -10);
  ctx.moveTo(24, -8);
  ctx.lineTo(25, -9.5);
  ctx.moveTo(36, -8.2);
  ctx.lineTo(37, -9.7);
  ctx.moveTo(47, -8.4);
  ctx.lineTo(48, -9.9);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // side grab rail aft of the console
  ctx.strokeStyle = c('C2CAD2');
  ctx.lineWidth = 0.9;
  ctx.globalAlpha = 0.85;
  ctx.beginPath();
  ctx.moveTo(-30, -8);
  ctx.lineTo(-30, -10);
  ctx.lineTo(-18, -10.2);
  ctx.lineTo(-18, -8.5);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // bow casting-deck step
  ctx.strokeStyle = c('B7C0C9');
  ctx.lineWidth = 0.6;
  ctx.globalAlpha = 0.6;
  ctx.beginPath();
  ctx.moveTo(18, -8);
  ctx.lineTo(18, -5);
  ctx.stroke();
  ctx.globalAlpha = 1;

  if (night > 0.04) {
    const alpha = Math.min(0.95, night * 1.05);
    palette.navGlow(58, -8, 6, '45,210,95', alpha);
    palette.navGlow(-7, -32, 6, '255,250,235', alpha);
  }
};
